<template>
  <div class="admin-page editor-usage-page">
    <div class="page-header">
      <div>
        <h2>工作台使用</h2>
        <p>了解有多少用户尝试新版，以及他们最近使用的工作台</p>
      </div>
      <div class="usage-filters">
        <el-radio-group :model-value="days" size="small" @change="changeDays">
          <el-radio-button :label="7">近 7 天</el-radio-button>
          <el-radio-button :label="30">近 30 天</el-radio-button>
          <el-radio-button :label="90">近 90 天</el-radio-button>
        </el-radio-group>
        <el-button size="small" :loading="loading" @click="loadData">刷新</el-button>
      </div>
    </div>

    <el-skeleton v-if="loading && !data" :rows="8" animated />
    <el-result v-else-if="error" icon="warning" title="使用数据加载失败" sub-title="请检查服务连接后重试。暂时无法读取数据不代表使用人数为零。">
      <template #extra><el-button type="primary" @click="loadData">重新加载</el-button></template>
    </el-result>
    <div v-else-if="data" v-loading="loading">
      <div class="usage-period">{{ data.range.startDate }} — {{ data.range.endDate }} · 北京时间 · 登录账号去重</div>
      <div class="usage-metrics">
        <article class="usage-metric">
          <p>工作台访问人数</p><strong>{{ number(summary.totalUsers) }}<small>人</small></strong>
          <span>共打开编辑器 {{ number(summary.opens) }} 次</span>
        </article>
        <article class="usage-metric is-new">
          <p>新版尝试率</p><strong>{{ percent(summary.workbenchUsers) }}<small v-if="summary.totalUsers">%</small></strong>
          <span>{{ number(summary.workbenchUsers) }} 人访问过新版</span>
        </article>
        <article class="usage-metric is-new">
          <p>最近使用新版的比例</p><strong>{{ percent(summary.latestWorkbenchUsers) }}<small v-if="summary.totalUsers">%</small></strong>
          <span>{{ number(summary.latestWorkbenchUsers) }} 人在区间内最后一次使用新版</span>
        </article>
        <article class="usage-metric">
          <p>切回经典的人数</p><strong>{{ number(summary.toClassicUsers) }}<small>人</small></strong>
          <span>从新版切回经典，共 {{ number(summary.toClassic) }} 次</span>
        </article>
      </div>

      <div class="usage-charts">
        <el-card shadow="never" class="usage-trend">
          <template #header><div class="usage-card-title"><h3>每日访问人数</h3><span>同一天使用两种工作台，会分别计入</span></div></template>
          <div v-show="summary.totalUsers" ref="chartRef" class="usage-chart" role="img" aria-label="经典和新版工作台每日去重访问人数趋势"></div>
          <el-empty v-if="!summary.totalUsers" description="所选时段还没有使用数据" :image-size="100" />
        </el-card>
        <el-card shadow="never" class="usage-latest">
          <template #header><div class="usage-card-title"><h3>最近使用分布</h3><span>按区间内最后一次访问或切换统计</span></div></template>
          <template v-if="summary.totalUsers">
            <div class="usage-split-bar" aria-hidden="true">
              <span class="is-classic" :style="{ width: percent(summary.latestClassicUsers) + '%' }"></span>
              <span class="is-new" :style="{ width: percent(summary.latestWorkbenchUsers) + '%' }"></span>
            </div>
            <div class="usage-split-row"><span><i class="classic-dot"></i>经典工作台</span><b>{{ number(summary.latestClassicUsers) }} 人 <em>{{ percent(summary.latestClassicUsers) }}%</em></b></div>
            <div class="usage-split-row"><span><i class="new-dot"></i>新版工作台</span><b>{{ number(summary.latestWorkbenchUsers) }} 人 <em>{{ percent(summary.latestWorkbenchUsers) }}%</em></b></div>
            <p class="usage-latest-note">每人只计入一种。结合「新版尝试率」观察尝试之后的使用情况。</p>
          </template>
          <el-empty v-else description="有访问后显示分布" :image-size="100" />
        </el-card>
      </div>

      <el-card shadow="never" class="usage-comparison">
        <template #header><div class="usage-card-title"><h3>两种工作台对照</h3><span>访问人数可重叠，最近使用人数互斥</span></div></template>
        <el-table :data="comparison" style="width: 100%">
          <el-table-column prop="label" label="工作台" min-width="160" />
          <el-table-column label="访问人数" min-width="110"><template #default="{ row }">{{ number(row.users) }}</template></el-table-column>
          <el-table-column label="最近使用人数" min-width="140"><template #default="{ row }">{{ number(row.latest) }}</template></el-table-column>
          <el-table-column label="主动切入人数" min-width="140"><template #default="{ row }">{{ number(row.switchUsers) }}</template></el-table-column>
          <el-table-column label="主动切入次数" min-width="140"><template #default="{ row }">{{ number(row.switches) }}</template></el-table-column>
        </el-table>
      </el-card>

      <el-card shadow="never" class="usage-users">
        <template #header><div class="usage-card-title"><h3>用户使用明细</h3><span>按最近访问排序 · 所选时段内 {{ number(data.total) }} 位用户</span></div></template>
        <el-table :data="data.users" style="width: 100%" empty-text="暂无记录，数据将从功能上线后开始积累">
          <el-table-column label="用户" min-width="170">
            <template #default="{ row }"><router-link class="usage-user-link" :to="{ path: '/user/detail', query: { id: row.userId } }">{{ row.username || '未命名用户' }}</router-link><div class="usage-user-id">{{ row.userId }}</div></template>
          </el-table-column>
          <el-table-column label="最近使用" min-width="130"><template #default="{ row }"><el-tag :type="row.latestMode === 'workbench' ? 'success' : 'info'" size="small">{{ row.latestMode === 'workbench' ? '新版工作台' : '经典工作台' }}</el-tag></template></el-table-column>
          <el-table-column label="访问过的工作台" min-width="150"><template #default="{ row }">{{ [row.classic ? '经典' : '', row.workbench ? '新版' : ''].filter(Boolean).join('、') }}</template></el-table-column>
          <el-table-column prop="opens" label="打开次数" min-width="100" />
          <el-table-column prop="toWorkbench" label="切入新版" min-width="100" />
          <el-table-column prop="toClassic" label="切回经典" min-width="100" />
          <el-table-column label="最近访问时间" min-width="175"><template #default="{ row }">{{ formatTime(row.lastUsedAt) }}</template></el-table-column>
        </el-table>
        <div v-if="data.total" class="usage-pagination"><el-pagination :current-page="page" :page-size="20" :total="data.total" layout="total, prev, pager, next" @current-change="changePage" /></div>
      </el-card>
      <p class="usage-definition">统计口径：默认进入经典工作台。成功打开本人简历后记录访问，实际切换工作台时记录切换；刷新会增加打开次数，人数按账号去重。访问不等于完成编辑，「最近使用」也不等于留存率。数据从功能上线后开始积累，不回填历史。</p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import * as echarts from 'echarts'
import { getEditorUsage } from '@/api/resume'

const days = ref(30)
const page = ref(1)
const data = ref(null)
const loading = ref(false)
const error = ref(false)
const chartRef = ref(null)
const summary = computed(() => data.value?.summary || {})
const number = (value) => Number(value || 0).toLocaleString('zh-CN')
const percent = (value) => summary.value.totalUsers ? (100 * value / summary.value.totalUsers).toFixed(1) : '—'
const formatTime = (value) => new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
const comparison = computed(() => [
  { label: '经典工作台', users: summary.value.classicUsers, latest: summary.value.latestClassicUsers, switchUsers: summary.value.toClassicUsers, switches: summary.value.toClassic },
  { label: '新版工作台', users: summary.value.workbenchUsers, latest: summary.value.latestWorkbenchUsers, switchUsers: summary.value.toWorkbenchUsers, switches: summary.value.toWorkbench }
])
let chart
let requestId = 0
const drawChart = () => {
  chart?.dispose()
  chart = null
  if (!chartRef.value || !summary.value.totalUsers) return
  chart = echarts.init(chartRef.value)
  chart.setOption({
    color: ['#64748b', '#16836b'],
    tooltip: { trigger: 'axis', confine: true },
    legend: { bottom: 0, icon: 'roundRect', itemWidth: 14, itemHeight: 4 },
    grid: { left: 34, right: 16, top: 18, bottom: 58, containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: data.value.daily.map(row => row.date), axisLabel: { formatter: value => value.slice(5), color: '#64748b' }, axisLine: { lineStyle: { color: '#e2e8f0' } }, axisTick: { show: false } },
    yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [
      { name: '经典工作台', type: 'line', showSymbol: false, data: data.value.daily.map(row => row.classicUsers), lineStyle: { width: 2 } },
      { name: '新版工作台', type: 'line', showSymbol: false, data: data.value.daily.map(row => row.workbenchUsers), lineStyle: { width: 2.5 }, areaStyle: { color: '#16836b', opacity: 0.06 } }
    ]
  })
}
const loadData = async () => {
  const current = ++requestId
  loading.value = true
  error.value = false
  try {
    const response = await getEditorUsage({ days: days.value, page: page.value, limit: 20 })
    if (current !== requestId) return
    data.value = response
    await nextTick()
    if (current === requestId) drawChart()
  } catch {
    if (current === requestId) { error.value = true; data.value = null; chart?.dispose(); chart = null }
  } finally { if (current === requestId) loading.value = false }
}
const changeDays = (value) => { days.value = value; page.value = 1; data.value = null; loadData() }
const changePage = (value) => { page.value = value; loadData() }
const resize = () => chart?.resize()
let resizeObserver
onMounted(() => {
  loadData()
  window.addEventListener('resize', resize)
  resizeObserver = new ResizeObserver(resize)
  const main = document.querySelector('.editor-usage-page')
  if (main) resizeObserver.observe(main)
})
onBeforeUnmount(() => { requestId++; chart?.dispose(); resizeObserver?.disconnect(); window.removeEventListener('resize', resize) })
</script>

<style lang="scss" scoped>
.editor-usage-page { padding: 24px; }
.usage-filters { display: flex; align-items: center; gap: 10px; }
.usage-period { margin: 0 0 14px; color: #64748b; font-size: 12px; }
.usage-metrics { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 16px; margin-bottom: 20px; }
.usage-metric { border: 1px solid #e5e7eb; border-radius: 8px; background: white; padding: 20px;
  p { color: #475569; font-size: 13px; margin: 0 0 14px; }
  strong { display: block; font-size: 32px; line-height: 1.2; font-variant-numeric: tabular-nums; font-weight: 650; margin-bottom: 12px; color: #1e293b; }
  small { font-size: 14px; font-weight: 400; margin-left: 6px; }
  > span { color: #64748b; font-size: 12px; line-height: 1.6; }
  &.is-new { border-top: 3px solid #16836b; padding-top: 18px; strong { color: #16725f; } }
}
.usage-charts { display: grid; grid-template-columns: minmax(0,1.7fr) minmax(280px,1fr); gap: 20px; margin-bottom: 20px; }
.usage-card-title { h3 { margin: 0 0 5px; font-size: 15px; color: #334155; font-weight: 600; } > span { font-size: 12px; color: #64748b; line-height: 1.6; } }
.usage-chart { width: 100%; height: 260px; }
.usage-split-bar { display: flex; height: 16px; margin: 30px 0; border-radius: 4px; overflow: hidden; .is-classic { background: #64748b; } .is-new { background: #16836b; } }
.usage-split-row { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; margin: 20px 0; color: #334155;
  i { display: inline-block; width: 8px; height: 8px; margin-right: 8px; border-radius: 2px; }
  .classic-dot { background: #64748b; } .new-dot { background: #16836b; }
  em { display: inline-block; min-width: 54px; font-style: normal; font-weight: 400; color: #64748b; text-align: right; margin-left: 10px; }
}
.usage-latest-note { font-size: 12px; color: #64748b; line-height: 1.8; border-top: 1px solid #eef2f7; padding-top: 18px; margin-top: 25px; }
.usage-comparison, .usage-users { margin-bottom: 20px; }
.usage-user-link { color: #2563eb; font-size: 13px; }
.usage-user-id { color: #94a3b8; font-size: 11px; margin-top: 3px; }
.usage-pagination { display: flex; justify-content: flex-end; margin-top: 18px; overflow-x: auto; }
.usage-definition { color: #64748b; font-size: 12px; line-height: 1.9; margin: 4px 0 20px; }
@media (max-width: 1250px) { .usage-metrics { grid-template-columns: repeat(2,minmax(0,1fr)); } }
@media (max-width: 900px) { .usage-charts { grid-template-columns: minmax(0,1fr); } .page-header { flex-direction: column; } }
@media (max-width: 600px) { .editor-usage-page { padding: 16px; } .usage-metrics { gap: 10px; } .usage-metric { padding: 14px; &.is-new { padding-top: 12px; } strong { font-size: 26px; } } }
</style>
